import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import './UploadFilesPage.css';

interface UploadedFile {
  originalName: string;
  url: string;
}

const UploadFilesPage: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [failedFiles, setFailedFiles] = useState<any[]>([]);

  // WebP conversion states
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [converting, setConverting] = useState(false);
  const [webpError, setWebpError] = useState<string | null>(null);
  const [convertedWebpUrl, setConvertedWebpUrl] = useState<string | null>(null);
  const [originalImageName, setOriginalImageName] = useState<string>('');

  // Cleanup function to revoke object URLs on unmount
  useEffect(() => {
    return () => {
      if (convertedWebpUrl) {
        URL.revokeObjectURL(convertedWebpUrl);
      }
    };
  }, [convertedWebpUrl]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Convert FileList to array first
      const fileArray = Array.from(files);
      // Append new files to existing selected files
      setSelectedFiles(prev => [...prev, ...fileArray]);
      setError(null);
      setFailedFiles([]);

      // Reset the input value after processing to allow selecting the same file again
      setTimeout(() => {
        event.target.value = '';
      }, 0);
    }
  };

  const removeSelectedFile = (indexToRemove: number) => {
    setSelectedFiles(prev =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleImageFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] || null;
    setSelectedImageFile(file);
    setWebpError(null);
    // Store original image name for download
    if (file) {
      setOriginalImageName(file.name);
    }
    // Clear previous conversion result
    if (convertedWebpUrl) {
      URL.revokeObjectURL(convertedWebpUrl);
      setConvertedWebpUrl(null);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setFailedFiles([]);

      const formData = new FormData();
      selectedFiles.forEach(file => {
        // Sanitize filename to ASCII-safe format
        const sanitizedName = file.name
          .replace(/[^a-zA-Z0-9._-]/g, '_')
          .replace(/_{2,}/g, '_');
        const renamedFile = new File([file], sanitizedName, {
          type: file.type,
        });
        formData.append('files', renamedFile);
      });

      const response = await authService.uploadMultipleFiles(formData);

      // Add successfully uploaded files to the list with original names
      const uploadedFilesData: UploadedFile[] = response.uploaded_files.map(
        (file, index) => ({
          originalName: selectedFiles[index]?.name || 'Unknown file',
          url: file.file_url,
        })
      );
      setUploadedFiles(prev => [...prev, ...uploadedFilesData]);

      // Store failed files if any
      if (response.failed_files && response.failed_files.length > 0) {
        setFailedFiles(response.failed_files);
        setError(
          `${uploadedFilesData.length} file(s) uploaded successfully, ${response.failed_files.length} file(s) failed.`
        );
      }

      setSelectedFiles([]);
    } catch (error) {
      console.error('File upload failed:', error);
      setError('File upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleWebpConversion = async () => {
    if (!selectedImageFile) {
      setWebpError('Please select an image file');
      return;
    }

    try {
      setConverting(true);
      setWebpError(null);

      const formData = new FormData();
      // Create a new File object with a simple name to avoid special character issues
      const fileExtension = selectedImageFile.name.split('.').pop() || '';
      const renamedFile = new File(
        [selectedImageFile],
        `img.${fileExtension}`,
        {
          type: selectedImageFile.type,
        }
      );
      formData.append('file', renamedFile);

      const webpBlob = await authService.convertToWebp(formData);

      // Create a downloadable URL for the WebP blob
      const webpUrl = URL.createObjectURL(webpBlob);
      setConvertedWebpUrl(webpUrl);

      // Reset the image file input
      const imageInput = document.getElementById(
        'image-input'
      ) as HTMLInputElement;
      if (imageInput) {
        imageInput.value = '';
      }
      setSelectedImageFile(null);
    } catch (error) {
      console.error('WebP conversion failed:', error);
      setWebpError('WebP conversion failed. Please try again.');
    } finally {
      setConverting(false);
    }
  };

  const downloadWebp = () => {
    if (!convertedWebpUrl) return;

    const link = document.createElement('a');
    link.href = convertedWebpUrl;

    // Create filename by replacing the original extension with .webp
    const originalName = originalImageName || 'converted-image';
    const nameWithoutExt =
      originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
    link.download = `${nameWithoutExt}.webp`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const removeUploadedFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000); // Reset after 2 seconds
      console.log('Link copied to clipboard');
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getAcceptAttribute = () => {
    return 'image/*,video/*,audio/*,.lrc,.mp3,application/pdf,.pdf';
  };

  const getUploadText = () => {
    return 'Click to select image, video, audio, PDF, or LRC files, or drag and drop here';
  };

  const getUploadHint = () => {
    return 'Supported formats: Images (JPG, PNG, GIF, etc.), Videos (MP4, AVI, MOV, etc.), Audio (MP3, WAV, AAC, etc.), PDFs (.pdf), and LRC files (.lrc). You can select multiple files at once.';
  };

  return (
    <div className="upload-files-page">
      <div className="upload-container">
        {/* File Upload Area */}
        <div className="file-upload-area">
          <h2 className="section-title">Upload Files</h2>

          <div className="upload-zone">
            <input
              id="file-input"
              type="file"
              onChange={handleFileSelect}
              className="file-input"
              accept={getAcceptAttribute()}
              multiple
            />
            <div className="upload-zone-content">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17,8 12,3 7,8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <p className="upload-text">{getUploadText()}</p>
              <p className="upload-hint">{getUploadHint()}</p>
            </div>
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="selected-files">
              <h3 className="subsection-title">
                Selected Files ({selectedFiles.length}):
              </h3>
              <div className="file-list">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="file-item">
                    <div className="file-info">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <span className="file-type">
                        {file.type || 'Unknown'}
                      </span>
                      <button
                        className="remove-file-btn"
                        onClick={() => removeSelectedFile(index)}
                        title="Remove file"
                        style={{ marginLeft: '8px' }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          {selectedFiles.length > 0 && (
            <button
              className="upload-button"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <div className="loading-spinner"></div>
                  Uploading {selectedFiles.length} file(s)...
                </>
              ) : (
                <>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17,8 12,3 7,8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Upload {selectedFiles.length} File(s)
                </>
              )}
            </button>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            {error}
          </div>
        )}

        {/* Uploaded Files */}
        {uploadedFiles.length > 0 && (
          <div className="uploaded-files">
            <h2 className="section-title">Uploaded Files</h2>
            <div className="uploaded-files-list">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="uploaded-file-item">
                  <div className="file-url">
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={file.url}
                    >
                      {file.originalName}
                    </a>
                  </div>
                  <div className="file-actions">
                    <button
                      className={`copy-link-btn ${copiedIndex === index ? 'copied' : ''}`}
                      onClick={() => copyToClipboard(file.url, index)}
                      title="Copy link to clipboard"
                    >
                      {copiedIndex === index ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2 2v1" />
                        </svg>
                      )}
                    </button>
                    <button
                      className="remove-file-btn"
                      onClick={() => removeUploadedFile(index)}
                      title="Remove from list"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* WebP Conversion Area */}
        <div className="file-upload-area">
          <h2 className="section-title">Convert Image to WebP</h2>

          <div className="upload-zone">
            <input
              id="image-input"
              type="file"
              onChange={handleImageFileSelect}
              className="file-input"
              accept="image/*"
            />
            <div className="upload-zone-content">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
              <p className="upload-text">
                Click to select an image file or drag and drop here
              </p>
              <p className="upload-hint">
                Supported formats: JPG, PNG, GIF, BMP, TIFF, and other image
                formats
              </p>
            </div>
          </div>

          {/* Selected Image File */}
          {selectedImageFile && (
            <div className="selected-files">
              <h3 className="subsection-title">Selected Image:</h3>
              <div className="file-list">
                <div className="file-item">
                  <div className="file-info">
                    <span className="file-name">{selectedImageFile.name}</span>
                    <span className="file-size">
                      {formatFileSize(selectedImageFile.size)}
                    </span>
                  </div>
                  <span className="file-type">
                    {selectedImageFile.type || 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Convert Button */}
          {selectedImageFile && (
            <button
              className="upload-button"
              onClick={handleWebpConversion}
              disabled={converting}
            >
              {converting ? (
                <>
                  <div className="loading-spinner"></div>
                  Converting to WebP...
                </>
              ) : (
                <>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7,10 12,15 17,10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Convert to WebP
                </>
              )}
            </button>
          )}

          {/* WebP Error Message */}
          {webpError && (
            <div className="error-message">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {webpError}
            </div>
          )}

          {/* WebP Download Section */}
          {convertedWebpUrl && (
            <div className="webp-download-section">
              <h3 className="subsection-title">WebP Conversion Complete!</h3>
              <div className="webp-download-item">
                <div className="webp-info">
                  <span className="webp-name">
                    {originalImageName.substring(
                      0,
                      originalImageName.lastIndexOf('.')
                    ) || 'converted-image'}
                    .webp
                  </span>
                  <span className="webp-ready">Ready for download</span>
                </div>
                <button
                  className="download-webp-btn"
                  onClick={downloadWebp}
                  title="Download WebP file"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7,10 12,15 17,10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download WebP
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadFilesPage;
